if (!customElements.get('coming-soon-notify-bell')) {
  class ComingSoonNotifyBell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const trigger = this.querySelector('.cs-notify-bell__trigger');
      const dialog = this.querySelector('dialog');
      const close = this.querySelector('.cs-notify-bell__close');
      const form = this.querySelector('form');
      const email = this.querySelector('[name="email"]');
      const submit = this.querySelector('[type="submit"]');
      const status = this.querySelector('.cs-notify-bell__status');

      if (!trigger || !dialog || !form) return;

      let sending = false;
      let completed = false;
      let mobilePageState = null;

      const updateMobileViewport = () => {
        const viewport = window.visualViewport;

        dialog.style.setProperty(
          '--cs-modal-height',
          `${viewport ? viewport.height : window.innerHeight}px`
        );

        dialog.style.setProperty(
          '--cs-modal-top',
          `${viewport ? viewport.offsetTop : 0}px`
        );
      };

      const lockMobilePage = () => {
        if (
          mobilePageState ||
          !window.matchMedia('(max-width: 749px)').matches
        ) {
          return;
        }

        const body = document.body;
        const root = document.documentElement;
        const properties = ['position', 'top', 'left', 'width', 'overflow'];

        mobilePageState = {
          x: window.scrollX,
          y: window.scrollY,
          body: properties.map((name) => [
            name,
            body.style.getPropertyValue(name),
            body.style.getPropertyPriority(name)
          ]),
          rootOverflow: root.style.overflow
        };

        root.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${mobilePageState.y}px`;
        body.style.left = `-${mobilePageState.x}px`;
        body.style.width = '100%';
        body.style.overflow = 'hidden';

        updateMobileViewport();

        window.visualViewport?.addEventListener(
          'resize',
          updateMobileViewport
        );

        window.visualViewport?.addEventListener(
          'scroll',
          updateMobileViewport
        );
      };

      const unlockMobilePage = () => {
        if (!mobilePageState) return;

        const state = mobilePageState;
        mobilePageState = null;

        window.visualViewport?.removeEventListener(
          'resize',
          updateMobileViewport
        );

        window.visualViewport?.removeEventListener(
          'scroll',
          updateMobileViewport
        );

        state.body.forEach(([name, value, priority]) => {
          if (value) {
            document.body.style.setProperty(name, value, priority);
          } else {
            document.body.style.removeProperty(name);
          }
        });

        const root = document.documentElement;
        const scrollBehavior = root.style.scrollBehavior;

        root.style.overflow = state.rootOverflow;
        root.style.scrollBehavior = 'auto';

        window.scrollTo(state.x, state.y);

        root.style.scrollBehavior = scrollBehavior;

        dialog.style.removeProperty('--cs-modal-height');
        dialog.style.removeProperty('--cs-modal-top');
      };

      const announce = (message) => {
        status.textContent = '';
        requestAnimationFrame(() => {
          status.textContent = message;
        });
      };

      this.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      this.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });

      email.addEventListener('mousedown', (event) => {
        if (
          window.matchMedia(
            '(max-width: 749px), (hover: none)'
          ).matches
        ) {
          event.stopPropagation();
        }
      });

      email.addEventListener('click', (event) => {
        if (
          !window.matchMedia(
            '(max-width: 749px), (hover: none)'
          ).matches
        ) {
          return;
        }

        event.stopPropagation();

        if (
          !email.disabled &&
          !email.readOnly &&
          !email.closest('[inert]')
        ) {
          email.focus({ preventScroll: true });
        }
      });

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const card = this.closest('product-card');
        const cardTitle = card
          ? card.querySelector('[ref="productTitleLink"] .text-block > p')
          : null;
        const modalTitle = dialog.querySelector('.waitlist-product-label');

        if (cardTitle && modalTitle) {
          const typography = getComputedStyle(cardTitle);

          modalTitle.style.fontFamily = typography.fontFamily;
          modalTitle.style.fontSize = typography.fontSize;
          modalTitle.style.fontWeight = typography.fontWeight;
          modalTitle.style.fontStyle = typography.fontStyle;
          modalTitle.style.lineHeight = typography.lineHeight;
          modalTitle.style.letterSpacing = typography.letterSpacing;
          modalTitle.style.textTransform = typography.textTransform;
        }

        if (!dialog.open) {
          const touchLayout = window.matchMedia(
            '(hover: none), (pointer: coarse)'
          ).matches;

          if (touchLayout) {
            close.setAttribute('autofocus', '');
          }

          lockMobilePage();
          dialog.showModal();
          dialog.scrollTop = 0;

          if (touchLayout) {
            close.focus({ preventScroll: true });
            close.removeAttribute('autofocus');
          }
        }
      });

      close.addEventListener('click', () => {
        if (!sending) dialog.close();
      });

      dialog.addEventListener('cancel', (event) => {
        if (sending) event.preventDefault();
      });

      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog || sending) return;

        const bounds = dialog.getBoundingClientRect();
        const outside =
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom;

        if (outside) dialog.close();
      });

      dialog.addEventListener('close', () => {
        unlockMobilePage();
        trigger.focus({ preventScroll: true });
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (sending || completed || !form.reportValidity()) return;

        const selectedSize = form.querySelector('[name="size"]');

        if (!selectedSize || !selectedSize.value) return;

        sending = true;
        submit.disabled = true;
        submit.textContent = 'Sending…';
        close.disabled = true;
        form.setAttribute('aria-busy', 'true');
        announce('');

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);

        try {
          const response = await fetch(this.dataset.workerUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: email.value.trim(),
              product_handle: this.dataset.productHandle,
              variant_title: selectedSize.value
            }),
            signal: controller.signal
          });

          if (!response.ok) {
            throw new Error(
              'Waitlist request failed: HTTP ' + response.status
            );
          }

          if (
            (response.headers.get('content-type') || '').includes(
              'application/json'
            )
          ) {
            const result = await response.json();

            if (
              result &&
              (result.success === false || result.error)
            ) {
              throw new Error('Waitlist request was rejected');
            }
          }

          completed = true;
          submit.textContent = 'Registered ✓';
          email.readOnly = true;

          form.querySelectorAll('[name="size"]').forEach((input) => {
            input.disabled = true;
          });

          status.dataset.state = 'success';
          announce(this.dataset.successMessage);
        } catch (error) {
          console.error('Coming Soon bell signup failed:', error);
          submit.disabled = false;
          submit.textContent = 'Register Interest';
          announce(this.dataset.errorMessage);
        } finally {
          clearTimeout(timeout);
          sending = false;
          close.disabled = false;
          form.removeAttribute('aria-busy');
        }
      });
    }
  }

  customElements.define(
    'coming-soon-notify-bell',
    ComingSoonNotifyBell
  );
}
